import { type ReactElement, type ReactNode, useId } from "react";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";

import { Trash2Icon } from "lucide-react";
import {
  type Control,
  type FieldArrayPath,
  type FieldValues,
  type Path,
  useFieldArray,
  useFormState,
} from "react-hook-form";

import { TextInput } from "../TextInput/TextInput";

type HeadersInputProps<T extends FieldValues> = {
  /** Path to the headers field, e.g. "headers" */
  fieldName: FieldArrayPath<T>;
  label?: string;
  hideLabel?: boolean;
  hideError?: boolean;
  control: Control<T>;
  disabled?: boolean;
  /** Optional text for the “add header” button */
  addLabel?: ReactNode;
};

export const KeyValueInput = <T extends FieldValues>({
  fieldName,
  label = "Headers",
  hideLabel = false,
  hideError = false,
  control,
  disabled,
  addLabel = "Add header",
}: HeadersInputProps<T>): ReactElement => {
  const id = useId();

  const { fields, append, remove } = useFieldArray({
    name: fieldName,
    control,
  });

  const { errors } = useFormState({ control, name: fieldName as Path<T> });

  const arrayError = (errors as any)?.[fieldName];

  return (
    <>
      {!hideLabel && <Typography id={id}>{label}</Typography>}
      <FormControl fullWidth error={!!arrayError} size="small">
        <Stack spacing={1}>
          {fields.map((field, index) => (
            <Stack
              key={field.id}
              direction="row"
              spacing={1}
              sx={{ alignItems: "center" }}
            >
              <Box sx={{ flex: 1 }}>
                <TextInput<T>
                  control={control}
                  fieldName={`${fieldName}.${index}.key` as Path<T>}
                  label={`${label} ${index} key`}
                  hideLabel
                  fullWidth
                  disabled={disabled}
                />
              </Box>

              <Box sx={{ flex: 1 }}>
                <TextInput<T>
                  control={control}
                  fieldName={`${fieldName}.${index}.value` as Path<T>}
                  label={`${label} ${index} value`}
                  hideLabel
                  fullWidth
                  disabled={disabled}
                />
              </Box>

              <IconButton
                onClick={() => remove(index)}
                edge="end"
                disabled={disabled}
              >
                <Trash2Icon fontSize="small" />
              </IconButton>
            </Stack>
          ))}

          <Button
            variant="outlined"
            size="small"
            onClick={() => append({ key: "", value: "" } as any)}
            sx={{ alignSelf: "flex-start", mt: 0.5 }}
            disabled={disabled}
          >
            {addLabel}
          </Button>
        </Stack>

        {!hideError && arrayError?.message && (
          <FormHelperText>{arrayError.message as string}</FormHelperText>
        )}
      </FormControl>
    </>
  );
};
